<template>
    <div :class="getClass(localState, localSize)">
        <div class="flex items-center justify-start gap-x-2 py-1 font-medium">
            <component
                :is="localIcon"
                v-if="typeof localIcon === 'function'"
                :class="config.icon"
            />
            <span v-if="title.length > 0">{{ localTitle }}</span>
        </div>
        <slot></slot>
    </div>
</template>
<script setup>
    import { ref, watch } from 'vue';

    defineExpose({
        name: 'AlertBox',
    });

    const props = defineProps({
        state: {
            type: String,
            default: 'neutral',
        },
        icon: {
            type: Function || Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        size: {
            type: String,
            default: 'md',
        },
        classOverride: {
            type: String,
            default: '',
        },
    });

    const AlertState = [
        'brand',
        'promo',
        'primary',
        'info',
        'success',
        'warning',
        'danger',
        'neutral',
    ];
    const AlertSize = ['xs', 'sm', 'md', 'lg', 'xl'];

    const localState = AlertState.includes(props.state) ? props.state : ref('neutral');
    const localIcon = ref(props.icon);
    const localTitle = ref(props.title);
    const localSize = AlertSize.includes(props.size) ? ref(props.size) : ref('md');
    const localClassOverride = ref(props.classOverride);

    const config = {
        default: 'transition-colors rounded-md border border-transparent block font-normal',
        icon: 'inline w-5 h-5',
        size: {
            xs: 'text-base py-1 px-2',
            sm: 'text-base py-1 px-2',
            md: 'text-base py-2 px-3 md:py-3 md:px-4',
            lg: 'text-base py-2 px-3 md:py-3 md:px-4 lg:py-4 lg:px-5',
            xl: 'text-base py-2 px-3 md:py-3 md:px-4 lg:py-4 lg:px-5 xl:py-5 xl-px-6',
        },
        state: {
            brand: 'bg-gradient-to-tr from-emerald-200 via-sky-200 to-blue-200 text-sky-600 dark:from-emerald-700 dark:via-sky-700 dark:to-blue-700 dark:text-sky-200',
            promo: 'bg-indigo-200 text-indigo-600 dark:bg-indigo-800 dark:text-indigo-200',
            primary: 'bg-blue-200 text-blue-600 dark:bg-blue-800 dark:text-blue-200',
            info: 'bg-cyan-200 text-cyan-600 dark:bg-cyan-700 dark:text-cyan-200',
            success: 'bg-emerald-200 text-emerald-600 dark:bg-emerald-800 dark:text-emerald-200',
            warning: 'bg-amber-200 text-amber-600 dark:bg-amber-800 dark:text-amber-200',
            danger: 'bg-red-200 text-red-600 dark:bg-red-800 dark:dark:text-red-200',
            neutral: 'bg-gray-200 text-gray-500 dark:bg-gray-800 dark:text-gray-300',
        },
    };

    const getClass = (state, size) => {
        return `${config.default} ${config.size[size]} ${config.state[state]} ${localClassOverride.value}`;
    };

    watch(
        () => props.state,
        (newValue) => {
            localState.value = newValue;
        },
    );
    watch(
        () => props.icon,
        (newValue) => {
            localIcon.value = newValue;
        },
    );
    watch(
        () => props.title,
        (newValue) => {
            localTitle.value = newValue;
        },
    );
    watch(
        () => props.size,
        (newValue) => {
            localSize.value = newValue;
        },
    );
    watch(
        () => props.classOverride,
        (newValue) => {
            localClassOverride.value = newValue;
        },
    );
</script>
